import { cloneDeep, first, flatMap, flattenDeep } from 'lodash'
import moment from 'moment'
import { useState } from 'react'

import { startDateFormatString, VISIT_STATUSES } from '../js/constants'
import { checkChainCargo, determineTotalDurationDelays, updateCargoEnd } from './terminalVisitUtils'
import { isValidTimestamp } from './formUtils'

/**
 * Format duration between given timestamps in hours:minutes
 * @param startTs
 * @param endTs
 * @returns {string}
 */
export function getFormattedDurationString(startTs, endTs) {
  const isStartNumericValue = /^\d+$/.test(startTs) && isValidTimestamp(startTs)
  const isEndNumericValue = /^\d+$/.test(endTs) && isValidTimestamp(endTs)

  if (!isStartNumericValue || !isEndNumericValue) {
    return 'Fill in a valid cargo start date and cargo end date'
  }

  const duration = endTs - startTs

  // Format the duration to hours:minutes
  const minutes = ('0' + (Math.floor(duration / (1000 * 60)) % 60)).slice(-2)
  const hours = Math.floor(duration / (1000 * 60 * 60))

  return `${hours}:${minutes} Hrs`
}

/**
 * Format a date as string, or return undefined when date is not a number
 * @param {number | undefined | null} date
 * @returns {string | undefined}
 */
export function getFormattedDate(date) {
  return typeof date === 'number' ? moment(date).format(startDateFormatString) : undefined
}

/**
 * Get all quay loadstations
 * @param {Quay} quay
 * @return {(Jetty | Mobru)[]}
 */
export function getAllQuayLoadstations(quay) {
  const jetties = quay.jetties ? quay.jetties : []
  const mobrus = quay.mobrus ? quay.mobrus : []

  return [...jetties, ...mobrus]
}

export function getAllLoadstationsAtQuays(quays) {
  return quays.map(quay => getAllQuayLoadstations(quay)).reduce((acc, val) => acc.concat(val), [])
}

export function getAllProductsAtQuays(quays) {
  if (!quays) return []
  return quays.map(quay => getAllProductsAtQuay(quay)).reduce((acc, val) => acc.concat(val), [])
}

export function getProductUnit(products, productString) {
  const selectedProduct = products.find(item => item.product === productString)
  return selectedProduct ? selectedProduct.productUnit || 'mt' : 'mt'
}

export function getUniqueListBy(arr, key) {
  if (!arr) return []
  return [...new Map(arr.map(item => [item[key], item])).values()]
}

export function getAllProductsAtQuay(quay) {
  const loadStationsAtQuay = getAllQuayLoadstations(quay)
  return flattenDeep(loadStationsAtQuay.map(loadStation => loadStation.products))
}

/**
 * Get all berth loadstations
 * @param {TerminalPlannerBerth} berth
 * @param {Quay[]} quayList
 * @param {Product['product']} productToLoad
 */
export function getLoadstationAtBerth(berth, quayList, productToLoad) {
  const quayThatContainsBerth = quayList.find(quay =>
    quay.berths.find(quayBerth => quayBerth.berthId === berth.berthId) ? true : false
  )
  const allLoadStationsAtQuay = getAllQuayLoadstations(quayThatContainsBerth)
  const loadStationInBerth = getLoadstationsBetweenBollards(allLoadStationsAtQuay, berth.startBollard, berth.endBollard)

  if (productToLoad) {
    return loadStationInBerth.find(loadStation =>
      loadStation.products.find(prod => prod.product === productToLoad) ? true : false
    )
  }
  return loadStationInBerth[0]
  // return berths.map(quay => getAllProductsAtBerth(quay)).reduce((acc, val) => acc.concat(val), [])
}

/**
 * Get the load station matching the product, don't know why the
 * @param {Quay[]} quays
 * @param {Product['product']} product
 */
export function getSlowestLoadStationOfProduct(quays, product) {
  const loadStations = cloneDeep(quays)
    .map(quay => getAllQuayLoadstations(quay))
    .reduce((acc, val) => acc.concat(val), [])

  // map to {matchingProduct, loadStation}[]
  const matchingLoadStations = loadStations.map(loadStation => {
    const matchingProduct = loadStation.products
      .filter(p => p.product === product)
      .sort((a, b) => (a.loadSpeed < b.loadSpeed ? -1 : 1))[0]
    return { matchingProduct, loadStation }
  })

  const slowestMatchingLoadStation = matchingLoadStations
    .filter(mls => mls.matchingProduct)
    .sort((a, b) => (a.matchingProduct.loadSpeed < b.matchingProduct.loadSpeed ? -1 : 1))
  return slowestMatchingLoadStation && slowestMatchingLoadStation[0]
    ? slowestMatchingLoadStation[0].loadStation
    : undefined
}

/**
 *
 * @param {Quay[]} quays
 * @return {TerminalPlannerBerth[]}
 */
export function getAllBerthsAtQuays(quays) {
  return flatMap(quays, quay => quay.berths)
}

/**
 * Get all loadstations between bollards
 * @param {(Jetty | Mobru)[]} loadStations
 * @param {number} bollardStart
 * @param {number} bollardEnd
 * @return {(Jetty | Mobru)[]}
 */
export function getLoadstationsBetweenBollards(loadStations, bollardStart, bollardEnd) {
  // Find which loading stations the terminal visit is at
  return loadStations.filter(loadStation => {
    // Only Jetty has field 'bollard', fix typing allowing to check on bollard
    // @ts-ignore
    if (typeof loadStation.bollard === 'number') {
      // include start and end for jetties
      // @ts-ignore
      return loadStation.bollard >= bollardStart && loadStation.bollard <= bollardEnd
    } else {
      // Mobru
      // @ts-ignore
      return loadStation.bollardStart < bollardEnd && loadStation.bollardEnd > bollardStart
    }
  })
}

export function getParsedValue(rawValue) {
  return isNaN(Number(rawValue)) || isNaN(parseFloat(rawValue)) ? null : parseFloat(rawValue)
}

export function getGridTime(time) {
  if (time.minutes() < 30) {
    return time.startOf('hour').valueOf()
  } else {
    return time.endOf('hour').valueOf() + 1
  }
}

/**
 * Determine the duration based on product and amount of the product amount
 * @param product
 * @param {'loading' | 'unloading'} operation
 * @param productInformation
 */
export function findMatchingProduct(product, operation, productInformation) {
  return getMatchingProducts(product, operation, productInformation).sort((a, b) => {
    return a.loadSpeed - b.loadSpeed
  })[0]
}

export function copyTerminalVisitAndReplaceOrders(visit, order, config) {
  const visitCopy = copyTerminalVisit(visit)
  const orderCopy = copyOrder(order)
  visitCopy.orders = [orderCopy]

  // move the terminal visit down, the new cargoStart is the old cargoEnd
  const chainCargo = checkChainCargo(order, config, determineTotalDurationDelays(visit))
  orderCopy.cargoStart = order.cargoEnd
  if (chainCargo) {
    updateCargoEnd(orderCopy, config, 0)
  } else {
    const duration = order.cargoEnd - order.cargoStart
    orderCopy.cargoEnd = orderCopy.cargoStart + duration
  }

  return visitCopy
}

export function copyTerminalVisit(visit) {
  const visitCopy = cloneDeep(visit)

  // Delete the properties like _id and ata
  cleanTerminalVisit(visitCopy)

  return visitCopy
}

export function copyOrder(order) {
  const orderCopy = cloneDeep(order)
  delete orderCopy.uuid

  return orderCopy
}

// helper function to find the currently selected order
export function findOrderByUuid(terminalVisit, orderUuid) {
  return orderUuid ? terminalVisit.orders.find(order => order.uuid === orderUuid) : first(terminalVisit.orders)
}

/**
 * Split a terminal visit with only one order
 * @param {TerminalVisit} terminalVisit
 */
export function splitTerminalVisit(terminalVisit) {
  if (terminalVisit.orders.length > 1) {
    throw new Error('Cannot split a terminal visit with multiple orders')
  }

  const current = cloneDeep(terminalVisit)
  const copy = cloneDeep(terminalVisit)

  // Delete the following properties to make it unique
  cleanTerminalVisit(copy)

  const currentOrder = first(current.orders)
  const copyOrder = first(copy.orders)
  delete copyOrder.uuid

  // Divide the cargo amount by half
  currentOrder.amount = Math.ceil(currentOrder.amount / 2)
  copyOrder.amount = Math.floor(copyOrder.amount / 2)

  // FIXME: the following code doesn't reckon with slack time. Use updateCargoEnd here
  // move the product times, the endtime is the starttime + duration divided by half
  const duration = (currentOrder.cargoEnd - currentOrder.cargoStart) / 2

  currentOrder.cargoEnd = currentOrder.cargoStart + duration
  copyOrder.cargoStart = currentOrder.cargoEnd
  copyOrder.cargoEnd = copyOrder.cargoStart + duration

  return { current, copy }
}

/**
 * Clean terminal visit times, delays and statjs
 * @param {TerminalVisit} terminalVisit
 */
function cleanTerminalVisit(terminalVisit) {
  delete terminalVisit._id
  delete terminalVisit.etaTime
  delete terminalVisit.ataTime
  delete terminalVisit.atdTime
  terminalVisit.delays = []

  terminalVisit.status = terminalVisit.ship.mmsi ? VISIT_STATUSES.assigned : VISIT_STATUSES.planned
}

/**
 *
 * @param {string} productName
 * @param {'loading' | 'unloading'} operation
 * @param {Product[]} allProducts
 * @param matchSubstring {boolean} indicates whether products should match if they contain the given productName as a substring
 */
export function getMatchingProducts(productName, operation, allProducts, matchSubstring = false) {
  const isUnloading = operation === 'unloading'

  return allProducts.filter(
    product =>
      typeof productName === 'string' &&
      (matchSubstring
        ? product.product.toLowerCase().includes(productName.toLowerCase())
        : product.product.toLowerCase() === productName.toLowerCase()) &&
      (product.isUnloading === isUnloading ||
        (product.isUnloading === undefined && isUnloading === false) ||
        (product.isUnloading === false && isUnloading === undefined))
  )
}

/**
 * See if eta still is valid based on eta time and last eta update
 * @param {number} eta
 * @param {number} etaTimeUpdatedAt
 * @return {boolean}
 */
export function isEtaStillValid(eta, etaTimeUpdatedAt) {
  return (
    eta &&
    eta > moment().subtract(15, 'minutes').valueOf() &&
    etaTimeUpdatedAt > moment().subtract(3, 'hours').valueOf()
  )
}

export function isNullOrUndefined(value) {
  return value === null || value === undefined
}

export function isNumber(value) {
  return typeof value === 'number' && !isNaN(value)
}

export function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value)
}

export function useModalState(initialState) {
  const [isOpen, setIsOpen] = useState(initialState || false)

  function openModal() {
    setIsOpen(true)
  }
  function closeModal() {
    setIsOpen(false)
  }

  return { isOpen, openModal, closeModal }
}

/**
 * Get decimal length
 * @param {string | number} value
 */
export function getDecimalLength(value) {
  const stringValue = String(value)
  const hasDecimal = stringValue.includes(',') || stringValue.includes('.')
  if (!hasDecimal) {
    return 0
  }

  return stringValue.replace(',', '.').split('.')[1].length
}

/**
 * Get time rounded in minutes
 * @param {number} timeInMiliSeconds
 */
export function roundToMinutes(timeInMiliSeconds) {
  return isValidTimestamp(timeInMiliSeconds)
    ? moment(timeInMiliSeconds).seconds(0).milliseconds(0).valueOf()
    : undefined
}
